<template>
  <div>
    <div :class="$style.pageWrapper">
      <div :class="$style.headerSection">
        <div :class="$style.actionsSection">
          <div>
            <InputText
              v-model="search"
              placeholder="Search campaigns"
              :class="$style.searchInput"
              @input="handleSearchCampaigns"
            />
            <Button
              label="New"
              icon="pi pi-chevron-down"
              icon-pos="right"
              :disabled="!isWipVersion"
              @click="toggleNewOptions"
            />
            <Popover ref="newOptionsRef">
              <div :class="$style.popoverOptions">
                <div @click="redirectToCreateCampaignForm">New campaign</div>
                <div @click="showCreateOfferModal">New offer</div>
              </div>
            </Popover>
            <Button
              label="CSV"
              icon="pi pi-chevron-down"
              icon-pos="right"
              :disabled="!isWipVersion"
              @click="toggleCsvOptions"
            />
            <Popover ref="csvOptionsRef">
              <div :class="$style.popoverOptions">
                <div @click="csvDropdownChange({ value: 'upload' })">Upload CSV</div>
                <div @click="csvDropdownChange({ value: 'download' })">Download Template</div>
              </div>
            </Popover>
          </div>
        </div>
      </div>

      <FileUpload
        ref="csvFileUpload"
        :key="csvFileUploadKey"
        :class="$style.uploadButton"
        :max-file-size="1048576"
        :file-limit="1"
        mode="basic"
        name="file"
        accept="text/csv"
        choose-label="Upload CSV"
        custom-upload
        :auto="true"
        style="display: none"
        @uploader="csvHandleFileUpload"
      />
      <ProgressSpinner v-if="!hasFilteredCampaigns" style="stroke: var(--vd-color-blue-default)" />
      <VTEmptyState
        v-else-if="searchedCampaigns.length === 0 && offersToDisplay.length === 0"
        :title="emptyState.title"
        :description="emptyState.description"
        :button-text="emptyState.buttonText"
        @empty-state-action="showCreateCampaignModal"
      />
      <DataTable
        v-else
        :value="searchedCampaigns"
        :class="$style.dataTable"
        data-key="id"
        table-style="min-width: 60rem; overflow: visible;"
        :pt="{
          table: {
            class: 'campaign-list-table',
          },
        }"
        @row-click="(e) => redirectToCampaign(e.data.id)"
      >
        <template #empty>
          <div>No campaigns found</div>
        </template>
        <Column header="Campaign Name" style="width: 20%">
          <template #body="slotProps">
            <div :style="{ fontWeight: slotProps.data.id === 'noCampaign' ? 'bold' : '' }">
              <span>{{ slotProps.data.name }}</span>
            </div>
          </template>
        </Column>
        <Column header="Offers">
          <template #body="slotProps">
            <OfferByStatus
              v-for="offer in offersByStatus(slotProps.data.offers)"
              :key="offer.status"
              :status="offer.status"
              :status-count="offer.count"
              :show-label="false"
              :class="$style.statusChip"
            />
          </template>
        </Column>
        <Column header="Dates">
          <template #body="slotProps">
            <div v-if="slotProps.data.startDate && slotProps.data.endDate" :class="$style.offerDates">
              {{ formatDateTime(slotProps.data.startDate) }} - {{ formatDateTime(slotProps.data.endDate) }}
            </div>
          </template>
        </Column>
        <Column header="Purchase Limit">
          <template #body="slotProps">
            <div :class="$style.purchaseLimitWrapper">
              <div
                v-if="slotProps.data.purchaseLimit !== undefined && slotProps.data.purchaseLimit !== null"
                :class="$style.purchaseLimit"
              >
                <VdIcon store="fa" name="cart-xmark" />
                {{ slotProps.data.purchaseLimit }}
              </div>
            </div>
          </template>
        </Column>
        <Column>
          <template #body="slotProps">
            <i :class="['pi pi-chevron-right', $style.redirectIcon]" @click="redirectToCampaign(slotProps.data.id)"></i>
          </template>
        </Column>
      </DataTable>
    </div>
    <OfferForm
      :is-visible="offerModalVisible"
      :loading="creatingOffer"
      @save-offer="handleSaveOffer"
      @close="handleCreateOfferModalClose"
    />
    <CampaignForm :is-visible="isCampaignModalVisible" @close="handleCreateCampaignModalClose" />
    <CSVImportModal
      :is-visible="csvModalVisible"
      :loading="csvLoading"
      :errors="csvErrors"
      :count="csvCount"
      :success="csvSuccess"
      @close="csvHandleModalClose"
    />
  </div>
</template>

<script setup>
import { useCreateOfferMutation } from "@/graphql";
import links from "@/router/links";
import { useDebounceFn } from "@vueuse/core";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import FileUpload from "primevue/fileupload";
import InputText from "primevue/inputtext";
import Popover from "primevue/popover";
import ProgressSpinner from "primevue/progressspinner";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import VTEmptyState from "@/components/VTEmptyState";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import useToast from "@/hooks/useToastNotification";
import { useOffers } from "@/modules/Offers/hooks/useOffers";
import formatDateTime from "@/utils/formatDateTime";
import CSVImportModal from "./components/CSVImportModal.vue";
import CampaignForm from "./components/CampaignForm.vue";
import OfferByStatus from "./components/OfferByStatus.vue";
import OfferForm from "./components/OfferForm.vue";

import uploadCSV from "./uploadCSV";

const { toastSuccess, toastError } = useToast();
const route = useRoute();
const router = useRouter();
const gameId = ref(route.params.gameId);
const versionId = ref(route.params.versionId);

const csvFileUpload = ref(null);
const csvFileUploadKey = ref(0);
const csvModalVisible = ref(false);
const csvLoading = ref(false);
const csvErrors = ref([]);
const csvCount = ref(0);
const csvSuccess = ref(false);

const search = ref("");
const selectAll = ref(false);

const offerModalVisible = ref(false);
const isCampaignModalVisible = ref(false);

const selectedOffers = ref(new Map());
const newOptionsRef = ref();
const csvOptionsRef = ref();

const { refetchOffers, getCampaigns, sortedCampaigns, offersToDisplay } = useOffers(gameId.value, versionId.value);
const { mutate: createOffer, loading: creatingOffer } = useCreateOfferMutation({});

const sortedOffers = computed(() => sortedCampaigns.value.flatMap((campaign) => campaign.offers));
const isWipVersion = computed(() => versionId.value === "wip");

const showCreateOfferModal = () => {
  offerModalVisible.value = true;
};

const handleCreateOfferModalClose = () => {
  offerModalVisible.value = false;
};

const handleCreateCampaignModalClose = () => {
  isCampaignModalVisible.value = false;
};

const toggleNewOptions = (event) => {
  newOptionsRef.value.toggle(event);
};

const toggleCsvOptions = (event) => {
  csvOptionsRef.value.toggle(event);
};

const emptyState = {
  title: "No campaigns",
  description: "You have no campaigns for the time being, to create a new campaign, use the button below",
  buttonText: "New campaign",
};

const searchedCampaigns = ref([]);
const hasFilteredCampaigns = ref(false);

const handleSearchCampaigns = useDebounceFn((event) => {
  hasFilteredCampaigns.value = false;
  selectedOffers.value.clear();
  const searchValue = event.target.value;

  searchedCampaigns.value = sortedCampaigns.value.filter((campaign) =>
    campaign.name.toLowerCase().includes(searchValue.toLowerCase() || [])
  );

  searchedCampaigns.value.sort((a, b) => {
    if (a.id === "noCampaign") {
      return -1;
    }
    if (b.id === "noCampaign") {
      return 1;
    }
    return 0;
  });

  hasFilteredCampaigns.value = true;
}, 500);

const offersByStatus = (offers) => {
  const groupedOffers = offers.reduce((acc, offer) => {
    if (!acc[offer.status]) {
      acc[offer.status] = [];
    }
    acc[offer.status].push(offer);
    return acc;
  }, {});

  const publishedCount = groupedOffers.PUBLISHED ? groupedOffers.PUBLISHED.length : 0;
  const draftCount =
    (groupedOffers.DRAFT ? groupedOffers.DRAFT.length : 0) + (groupedOffers.PENDING ? groupedOffers.PENDING.length : 0);
  const terminatingCount =
    (groupedOffers.TERMINATING ? groupedOffers.TERMINATING.length : 0) +
    (groupedOffers.STOPPED ? groupedOffers.STOPPED.length : 0);

  return [
    { status: "published", count: publishedCount },
    { status: "draft", count: draftCount },
    { status: "terminating", count: terminatingCount },
  ];
};

const handleSaveOffer = async (offer) => {
  const res = await createOffer({
    appId: gameId.value,
    versionId: versionId.value,
    input: offer,
  });

  if (res?.data?.createOffer?.success) {
    handleCreateOfferModalClose();
    refetchOffers();
    toastSuccess({
      detail: `The offer ${offer.name} has been created successfully!`,
      summary: "Success",
      life: 3 * 1000,
    });
  } else {
    toastError({
      detail: res?.data?.createOffer?.details,
      life: 3 * 1000,
      summary: "Error",
    });
  }
};

const csvTemplateUrl =
  "https://docs.google.com/spreadsheets/d/14movdiHRbrcjUsToPmeYjesvtXMHZHPhIT_I7o5NWuM/edit?usp=sharing";

const csvDropdownChange = async (selectedValue) => {
  if (selectedValue.value === "download") {
    window.open(csvTemplateUrl, "_blank");
  } else if (selectedValue.value === "upload") {
    csvFileUpload.value.$el.querySelector('input[type="file"]').click();
  }
};

const csvHandleModalClose = () => {
  csvModalVisible.value = false;
  csvFileUploadKey.value += 1;
};

const csvHandleFileUpload = async (event) => {
  const file = event.files[0];

  csvLoading.value = true;
  csvModalVisible.value = true;

  const importCsv = await uploadCSV(gameId.value, versionId.value, file);

  csvLoading.value = false;

  csvSuccess.value = importCsv.success;
  csvCount.value = importCsv.count;
  csvErrors.value = importCsv.errors;

  if (importCsv.success) {
    await refetchOffers();
  }
};

const redirectToCampaign = (campaignId) => {
  router.push({
    name: links.offersAndStore.tabs.offerCampaigns.offerList,
    params: { campaignId, gameId: gameId.value, versionId: versionId.value },
  });
};

watch([sortedOffers, search], () => {
  handleSearchCampaigns({ target: { value: search.value } });
});

watch(
  selectedOffers.value,
  () => {
    if (selectedOffers.value.size === 0) {
      selectAll.value = false;
      return;
    }

    selectAll.value = selectedOffers.value.size === sortedOffers.value.length;
  },
  { immediate: true }
);

onMounted(async () => {
  await getCampaigns();
});

const redirectToCreateCampaignForm = () => {
  router.push({
    name: links.offersAndStore.tabs.offerCampaigns.createCampaign,
    params: { gameId: gameId.value, versionId: versionId.value },
  });
};
</script>

<style module lang="scss">
.pageWrapper {
  display: flex;
  flex-direction: column;
}

.purchaseLimitWrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.purchaseLimit {
  background-color: var(--vd-color-blue-gray-100);
  color: var(--vd-color-text-default);
  font-weight: 600;
  border-radius: 25px;
  padding: 4px 8px;

  svg {
    color: var(--vd-color-text-light-2);
  }
}
.headerSection {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.actionsSection {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  width: 100%;

  div {
    display: flex;
    align-items: center;

    &:last-child {
      gap: 1rem;
    }
  }
}

.selectOffersActions {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;
  border: 2px solid var(--vd-color-blue-default);
  border-radius: 4px;
  background: var(--vd-color-blue-light-9);
  color: var(--vd-color-blue-default);
  padding: 0.75rem;
  padding-left: 1rem;
  font-weight: bold;
}

.redirectIcon {
  color: var(--vd-color-text-light-2);
}

.draftWarning {
  font-weight: normal;
  margin-top: 0.5rem;
  color: var(--vd-color-error);
  font-size: 12px;
}

.popoverOptions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  div {
    cursor: pointer;
    padding: 0.5rem;

    &:hover {
      background: rgb(243, 244, 246);
    }
  }
}

.offerDates {
  color: var(--vd-color-text-light-2);
}
</style>

<style scoped>
:deep(.p-datatable) {
  border: 1px solid var(--vd-color-text-light-7);
  border-bottom: 1px;
  border-radius: 8px;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
  cursor: pointer;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  font-weight: 700;
  background: var(--vd-color-text-light-9);
}

:deep(.p-datatable-table-container) {
  border-radius: 8px;
}

:deep(.p-progressspinner) {
  height: 50vh !important;
}
</style>
